import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Input,
  Modal,
  Pill,
  SegmentedControl,
  Select,
  Sheet,
  Skeleton,
  SkeletonText,
  useToast,
} from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import { useNavigate, useSearchParams } from "react-router";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { joinLabel, safeMeetingUrl } from "../lib/meeting-link.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./schedule.css";

/* ─────────────────────────────── local types ───────────────────────────────
 * Declared here rather than extending the shared `Task`/`Project`-style types
 * in lib/api.ts, which don't know about events yet — same reasoning Work.tsx
 * and TaskDetail.tsx give for their own local shapes, and editing that shared
 * file is out of scope for this surface.
 */

type EventKind = "meeting" | "deadline" | "reminder" | "leave" | string;

type ResponseStatus = "pending" | "accepted" | "declined" | "tentative";

type Attendee = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  responseStatus: ResponseStatus;
};

type ScheduleEvent = {
  id: string;
  projectId: string | null;
  title: string;
  description: string | null;
  kind: EventKind;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  location: string | null;
  organiserId: string;
  organiserName: string | null;
  recurrenceRule: string | null;
  /**
   * Set when this row is one occurrence of a repeating event, naming the stored
   * event it was expanded from. Absent on ordinary one-off events.
   *
   * An occurrence has no row of its own, so its `id` is synthetic and addresses
   * nothing the API will accept. Every mutation must use `eventKey` below.
   */
  occurrenceOf?: string;
  /** For an occurrence: the slot the rule put it in, which stays its handle even after it is moved. */
  originalStart?: string;
  isException?: boolean;
  meetingUrl?: string | null;
  /** The zone the event was scheduled in; shown as a hint when it differs from the viewer's. */
  timeZone?: string | null;
  /** The event's default lead, in minutes (0 = none). */
  reminderMinutes?: number;
  /** The viewer's own override, or null/absent when they follow the event's. */
  myReminderMinutes?: number | null;
  version: number;
  attendees: Attendee[];
};

/**
 * The id the API will accept for this row.
 *
 * For a one-off event that is simply its id; for an expanded occurrence it is
 * the repeating event it came from, because that is the only row that exists.
 */
const eventKey = (event: { id: string; occurrenceOf?: string }) => event.occurrenceOf ?? event.id;

type ViewMode = "month" | "week" | "day" | "agenda";

const VIEW_MODES: ViewMode[] = ["month", "week", "day", "agenda"];

/** How far the agenda reaches at first, and how much each "Load more" adds. */
const AGENDA_CHUNK_DAYS = 14;

/** Where a reminder can be set: minutes before the start, 0 meaning none. */
const REMINDER_CHOICES: { value: number; label: string }[] = [
  { value: 0, label: "No reminder" },
  { value: 5, label: "5 minutes before" },
  { value: 15, label: "15 minutes before" },
  { value: 30, label: "30 minutes before" },
  { value: 60, label: "1 hour before" },
  { value: 1440, label: "1 day before" },
];

const DEFAULT_REMINDER_MINUTES = 15;

type EditScope = "this" | "following" | "all";

const isRepeating = (event: { recurrenceRule: string | null; occurrenceOf?: string }) =>
  !!event.recurrenceRule || !!event.occurrenceOf;

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Zones to offer, always including the ones already in play. */
function zoneChoices(...include: (string | null | undefined)[]): string[] {
  const all = new Set<string>(["UTC"]);
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  try {
    for (const zone of supported?.("timeZone") ?? []) all.add(zone);
  } catch {
    /* an older engine: the zones in play are still offered */
  }
  for (const zone of include) if (zone) all.add(zone);
  return [...all].sort();
}

/** "09:00 Europe/Berlin" when the event was set in a zone other than the viewer's, else null. */
function originalZoneHint(event: ScheduleEvent): string | null {
  if (!event.timeZone || event.timeZone === browserZone()) return null;
  try {
    const time = new Date(event.startsAt).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: event.timeZone,
    });
    return `${time} ${event.timeZone.replace(/_/g, " ")}`;
  } catch {
    return null;
  }
}

/**
 * A task with a due date, shown on the day it falls due.
 *
 * Deliberately not folded into `ScheduleEvent` — a task has no `endsAt`, no
 * attendees, and its "time" is a deadline rather than a scheduled block, so
 * giving it the same shape as an event would mean inventing values (a
 * duration, an organiser) for fields that do not apply. It renders
 * differently for the same reason: a chip naming the due date, not a
 * positioned block in the week view's hour grid.
 */
type DueTask = {
  id: string;
  title: string;
  status: string;
  priority: string;
  dueAt: string;
  projectCode: string | null;
};

/* ─────────────────────────────── date helpers ───────────────────────────────
 * Everything here operates on real Date objects rather than string slicing —
 * comparing ISO strings lexically breaks the moment two events cross a
 * timezone offset, and the spec is explicit that render must happen in the
 * viewer's local zone.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfWeek(date: Date): Date {
  const d = startOfDay(date);
  return addDays(d, -d.getDay());
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** The 7-column grid always shows whole weeks, so the leading/trailing days
 * from adjacent months round the range out to a Sunday-to-Saturday span. */
function monthGridRange(monthStart: Date): { start: Date; end: Date } {
  const start = startOfWeek(monthStart);
  const lastOfMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  const end = addDays(startOfWeek(lastOfMonth), 7);
  return { start, end };
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Parses `?date=YYYY-MM-DD` as a *local* calendar date — `new Date("2026-07-27")`
 * would parse as UTC midnight and can render as the previous day in a
 * negative-offset zone, which is exactly the string-date-math bug the spec
 * warns against. */
function parseDateParam(value: string | null): Date {
  if (value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (match) {
      const [, y, m, d] = match;
      const parsed = new Date(Number(y), Number(m) - 1, Number(d));
      if (!Number.isNaN(parsed.getTime())) return startOfDay(parsed);
    }
  }
  return startOfDay(new Date());
}

function formatDateParam(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatMonthTitle(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function formatWeekTitle(weekStart: Date): string {
  const weekEnd = addDays(weekStart, 6);
  const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
  const startLabel = weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const endLabel = weekEnd.toLocaleDateString(undefined, sameMonth ? { day: "numeric", year: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
  return `${startLabel} – ${endLabel}`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function formatDateTimeLong(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** `<input type="datetime-local">` wants "YYYY-MM-DDTHH:mm" in local time — not
 * `toISOString()`, which is UTC and would silently shift the displayed value. */
function toDateTimeLocalValue(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

/** The inverse: a local `datetime-local` string back to a real Date, still in
 * local time (the Date constructor already treats this form as local). */
function fromDateTimeLocalValue(value: string): Date {
  return new Date(value);
}

/* ─────────────────────────────── colour + labels ───────────────────────────────
 * `Pill`'s tone prop is `neutral|accent|ok|warn|danger|info` — it has no
 * "violet" tone, so events are coloured via a CSS class keyed off `kind`
 * rather than through `Pill` for the calendar chips. `Pill` is still used for
 * RSVP status in the detail panel, where its existing tones fit exactly.
 */

const KIND_DOT_CLASS: Record<string, string> = {
  meeting: "sch-kind--accent",
  deadline: "sch-kind--danger",
  reminder: "sch-kind--info",
  leave: "sch-kind--violet",
};

function kindClass(kind: string): string {
  return KIND_DOT_CLASS[kind] ?? "sch-kind--accent";
}

const KIND_OPTIONS: { value: string; label: string }[] = [
  { value: "meeting", label: "Meeting" },
  { value: "deadline", label: "Deadline" },
  { value: "reminder", label: "Reminder" },
  { value: "leave", label: "Leave" },
];

const KIND_LABEL: Record<string, string> = Object.fromEntries(KIND_OPTIONS.map((opt) => [opt.value, opt.label]));

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}

/**
 * A single letter is the only marker that fits a chip this cramped (11px
 * type, one line, already ellipsising the title) without pushing real
 * content out — an icon set was considered, but no icon in the design
 * system distinguishes all four kinds unambiguously (e.g. nothing reads as
 * "leave"), where a letter tied to the kind's own label always does.
 */
function kindAbbr(kind: string): string {
  return kindLabel(kind).charAt(0).toUpperCase();
}

const RESPONSE_TONE: Record<ResponseStatus, PillTone> = {
  pending: "neutral",
  accepted: "ok",
  declined: "danger",
  tentative: "warn",
};

const RESPONSE_LABEL: Record<ResponseStatus, string> = {
  pending: "Pending",
  accepted: "Accepted",
  declined: "Declined",
  tentative: "Tentative",
};

/* ──────────────────────────────────── month view ──────────────────────────────────── */

interface MonthCellProps {
  date: Date;
  inCurrentMonth: boolean;
  isToday: boolean;
  events: ScheduleEvent[];
  dueTasks: DueTask[];
  onOpenEvent: (event: ScheduleEvent) => void;
  onOpenDay: (date: Date) => void;
  onOpenTask: (task: DueTask) => void;
}

const MAX_VISIBLE_PER_CELL = 3;

function MonthCell({
  date,
  inCurrentMonth,
  isToday,
  events: dayEvents,
  dueTasks: dayTasks,
  onOpenEvent,
  onOpenDay,
  onOpenTask,
}: MonthCellProps) {
  // Events first — they hold a time slot, which makes them the more
  // committed thing on the day — then tasks due, oldest overdue first if the
  // range somehow includes a stale one.
  const items: ({ type: "event"; event: ScheduleEvent } | { type: "task"; task: DueTask })[] = [
    ...dayEvents.map((event) => ({ type: "event" as const, event })),
    ...dayTasks.map((task) => ({ type: "task" as const, task })),
  ];
  const visible = items.slice(0, MAX_VISIBLE_PER_CELL);
  const overflow = items.length - visible.length;

  return (
    <div className={`sch-month__cell ${inCurrentMonth ? "" : "is-dimmed"}`}>
      <button
        type="button"
        className={`sch-month__date ${isToday ? "is-today" : ""}`}
        onClick={() => onOpenDay(date)}
        aria-label={`${date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}${isToday ? ", today" : ""}`}
      >
        {date.getDate()}
      </button>

      <div className="sch-month__events">
        {visible.map((item) =>
          item.type === "event" ? (
            <button
              key={item.event.id}
              type="button"
              className={`sch-month__event ${kindClass(item.event.kind)}`}
              onClick={() => onOpenEvent(item.event)}
              title={item.event.title}
            >
              <span className="sch-kind-badge" aria-hidden="true">
                {kindAbbr(item.event.kind)}
              </span>
              {!item.event.allDay && <span className="sch-month__event-time">{formatTime(item.event.startsAt)}</span>}
              <span className="sch-month__event-title">
                <span className="sr-only">{kindLabel(item.event.kind)}: </span>
                {item.event.title}
              </span>
            </button>
          ) : (
            /*
             * Its own class rather than reusing `sch-month__event`: a task due
             * is not a scheduled block the way an event is, and looking
             * identical to one would claim a time commitment that does not
             * exist — this is a deadline, not an appointment.
             */
            <button
              key={item.task.id}
              type="button"
              className="sch-month__task"
              onClick={() => onOpenTask(item.task)}
              title={item.task.title}
            >
              <Icon name="check" size={10} />
              <span className="sch-month__event-title">{item.task.title}</span>
            </button>
          ),
        )}
        {overflow > 0 && (
          <button type="button" className="sch-month__more" onClick={() => onOpenDay(date)}>
            +{overflow} more
          </button>
        )}
      </div>
    </div>
  );
}

interface MonthViewProps {
  focusedDate: Date;
  events: ScheduleEvent[];
  dueTasks: DueTask[];
  onOpenEvent: (event: ScheduleEvent) => void;
  onOpenDay: (date: Date) => void;
  onOpenTask: (task: DueTask) => void;
}

function MonthView({ focusedDate, events: allEvents, dueTasks: allDueTasks, onOpenEvent, onOpenDay, onOpenTask }: MonthViewProps) {
  const monthStart = startOfMonth(focusedDate);
  const { start } = monthGridRange(monthStart);
  const today = startOfDay(new Date());

  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(start, i)), [start]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, ScheduleEvent[]>();
    for (const day of days) {
      const dayStart = day.getTime();
      const dayEnd = dayStart + DAY_MS;
      const matches = allEvents
        .filter((e) => new Date(e.startsAt).getTime() < dayEnd && new Date(e.endsAt).getTime() > dayStart)
        .sort((a, b) => (a.allDay === b.allDay ? new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime() : a.allDay ? -1 : 1));
      map.set(formatDateParam(day), matches);
    }
    return map;
  }, [days, allEvents]);

  // A task has one instant — its due date — so it belongs on exactly the one
  // day it falls in, unlike an event's overlap test above.
  const dueTasksByDay = useMemo(() => {
    const map = new Map<string, DueTask[]>();
    for (const task of allDueTasks) {
      const key = formatDateParam(startOfDay(new Date(task.dueAt)));
      const list = map.get(key);
      if (list) list.push(task);
      else map.set(key, [task]);
    }
    return map;
  }, [allDueTasks]);

  return (
    <div className="sch-month">
      <div className="sch-month__weekdays">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="sch-month__weekday">
            {label}
          </div>
        ))}
      </div>
      <div className="sch-month__grid">
        {days.map((day) => (
          <MonthCell
            key={formatDateParam(day)}
            date={day}
            inCurrentMonth={day.getMonth() === monthStart.getMonth()}
            isToday={isSameDay(day, today)}
            events={eventsByDay.get(formatDateParam(day)) ?? []}
            dueTasks={dueTasksByDay.get(formatDateParam(day)) ?? []}
            onOpenEvent={onOpenEvent}
            onOpenDay={onOpenDay}
            onOpenTask={onOpenTask}
          />
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────────────── week view ──────────────────────────────────── */

const WEEK_START_HOUR = 0;
const WEEK_END_HOUR = 24;
const HOUR_HEIGHT = 48; // px per hour, drives every positioned event's top/height

interface WeekViewProps {
  /** First day shown. */
  weekStart: Date;
  /** 7 for the week view, 1 for the day view. */
  dayCount?: number;
  events: ScheduleEvent[];
  dueTasks: DueTask[];
  onOpenEvent: (event: ScheduleEvent) => void;
  onOpenTask: (task: DueTask) => void;
}

function WeekView({ weekStart, dayCount = 7, events: allEvents, dueTasks: allDueTasks, onOpenEvent, onOpenTask }: WeekViewProps) {
  const today = startOfDay(new Date());
  const days = useMemo(() => Array.from({ length: dayCount }, (_, i) => addDays(weekStart, i)), [weekStart, dayCount]);
  const hours = useMemo(() => Array.from({ length: WEEK_END_HOUR - WEEK_START_HOUR }, (_, i) => WEEK_START_HOUR + i), []);

  // Same reasoning as MonthView: a task has one instant, so it lands in
  // exactly the one day it is due — the all-day row, alongside events with no
  // time slot, since a deadline has no duration to draw either.
  const dueTasksByDay = useMemo(() => {
    const map = new Map<string, DueTask[]>();
    for (const task of allDueTasks) {
      const key = formatDateParam(startOfDay(new Date(task.dueAt)));
      const list = map.get(key);
      if (list) list.push(task);
      else map.set(key, [task]);
    }
    return map;
  }, [allDueTasks]);

  const byDay = useMemo(() => {
    const allDay = new Map<string, ScheduleEvent[]>();
    const timed = new Map<string, ScheduleEvent[]>();
    for (const day of days) {
      const key = formatDateParam(day);
      const dayStart = day.getTime();
      const dayEnd = dayStart + DAY_MS;
      const matches = allEvents.filter((e) => new Date(e.startsAt).getTime() < dayEnd && new Date(e.endsAt).getTime() > dayStart);
      allDay.set(key, matches.filter((e) => e.allDay));
      timed.set(
        key,
        matches.filter((e) => !e.allDay),
      );
    }
    return { allDay, timed };
  }, [days, allEvents]);

  const hasAllDay = days.some(
    (d) => (byDay.allDay.get(formatDateParam(d)) ?? []).length > 0 || (dueTasksByDay.get(formatDateParam(d)) ?? []).length > 0,
  );

  return (
    <div className="sch-week" style={{ "--sch-cols": dayCount } as CSSProperties}>
      <div className="sch-week__header">
        <div className="sch-week__gutter-spacer" />
        {days.map((day) => (
          <div key={formatDateParam(day)} className={`sch-week__day-header ${isSameDay(day, today) ? "is-today" : ""}`}>
            <span className="sch-week__day-name">{day.toLocaleDateString(undefined, { weekday: "short" })}</span>
            <span className="sch-week__day-num">{day.getDate()}</span>
          </div>
        ))}
      </div>

      {hasAllDay && (
        <div className="sch-week__allday">
          <div className="sch-week__gutter-spacer" />
          {days.map((day) => {
            const key = formatDateParam(day);
            return (
              <div key={key} className="sch-week__allday-cell">
                {(byDay.allDay.get(key) ?? []).map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    className={`sch-week__allday-chip ${kindClass(event.kind)}`}
                    onClick={() => onOpenEvent(event)}
                    title={event.title}
                  >
                    <span className="sch-kind-badge" aria-hidden="true">
                      {kindAbbr(event.kind)}
                    </span>
                    <span className="sr-only">{kindLabel(event.kind)}: </span>
                    {event.title}
                  </button>
                ))}
                {(dueTasksByDay.get(key) ?? []).map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    className="sch-week__allday-chip sch-week__allday-chip--task"
                    onClick={() => onOpenTask(task)}
                    title={task.title}
                  >
                    <Icon name="check" size={11} />
                    {task.title}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}

      <div className="sch-week__scroll">
        <div className="sch-week__body" style={{ height: (WEEK_END_HOUR - WEEK_START_HOUR) * HOUR_HEIGHT }}>
          <div className="sch-week__gutter">
            {hours.map((hour) => (
              <div key={hour} className="sch-week__hour-label" style={{ height: HOUR_HEIGHT }}>
                {hour === 0 ? "" : new Date(2000, 0, 1, hour).toLocaleTimeString(undefined, { hour: "numeric" })}
              </div>
            ))}
          </div>

          {days.map((day) => {
            const key = formatDateParam(day);
            const dayStart = day.getTime();
            return (
              <div key={key} className="sch-week__day-column">
                {hours.map((hour) => (
                  <div key={hour} className="sch-week__hour-line" style={{ height: HOUR_HEIGHT }} />
                ))}
                {(byDay.timed.get(key) ?? []).map((event) => {
                  const evStart = new Date(event.startsAt).getTime();
                  const evEnd = new Date(event.endsAt).getTime();
                  // Clamp to the visible day: an event spanning midnight is
                  // still positioned meaningfully rather than overflowing the
                  // column or reporting a negative height.
                  const clampedStart = Math.max(evStart, dayStart);
                  const clampedEnd = Math.min(evEnd, dayStart + DAY_MS);
                  const startHour = (clampedStart - dayStart) / (60 * 60 * 1000);
                  const durationHours = Math.max((clampedEnd - clampedStart) / (60 * 60 * 1000), 0.5);

                  return (
                    <button
                      key={event.id}
                      type="button"
                      className={`sch-week__event ${kindClass(event.kind)}`}
                      style={{ top: startHour * HOUR_HEIGHT, height: durationHours * HOUR_HEIGHT - 2 }}
                      onClick={() => onOpenEvent(event)}
                      title={event.title}
                    >
                      <span className="sch-week__event-title">
                        <span className="sch-kind-badge" aria-hidden="true">
                          {kindAbbr(event.kind)}
                        </span>
                        <span className="sr-only">{kindLabel(event.kind)}: </span>
                        {event.title}
                      </span>
                      <span className="sch-week__event-time">{formatTime(event.startsAt)}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────── agenda view ──────────────────────────────────── */

interface AgendaViewProps {
  start: Date;
  /** How many days from `start` the loaded range covers. */
  days: number;
  events: ScheduleEvent[];
  dueTasks: DueTask[];
  loadingMore: boolean;
  onLoadMore: () => void;
  onOpenEvent: (event: ScheduleEvent) => void;
  onOpenTask: (task: DueTask) => void;
}

/**
 * A plain list of what is coming, one heading per day that has anything on it.
 *
 * Days with nothing are left out on purpose: an agenda is for reading ahead, and
 * fourteen "nothing" lines in a row bury the three that matter. A multi-day
 * event is listed under each day it covers, so scanning any one day shows
 * everything happening on it.
 */
function AgendaView({ start, days: dayCount, events, dueTasks, loadingMore, onLoadMore, onOpenEvent, onOpenTask }: AgendaViewProps) {
  const today = startOfDay(new Date());
  const groups = useMemo(() => {
    const out: { day: Date; events: ScheduleEvent[]; tasks: DueTask[] }[] = [];
    for (let i = 0; i < dayCount; i += 1) {
      const day = addDays(start, i);
      const dayStart = day.getTime();
      const dayEnd = dayStart + DAY_MS;
      const dayEvents = events
        .filter((e) => new Date(e.startsAt).getTime() < dayEnd && new Date(e.endsAt).getTime() > dayStart)
        .sort((a, b) => (a.allDay === b.allDay ? new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime() : a.allDay ? -1 : 1));
      const tasks = dueTasks.filter((t) => {
        const due = new Date(t.dueAt).getTime();
        return due >= dayStart && due < dayEnd;
      });
      if (dayEvents.length || tasks.length) out.push({ day, events: dayEvents, tasks });
    }
    return out;
  }, [start, dayCount, events, dueTasks]);

  const lastDay = addDays(start, dayCount - 1);

  return (
    <div className="sch-agenda">
      {groups.length === 0 && (
        <div className="sch-empty-banner">
          <EmptyState
            icon="calendar"
            title="Nothing coming up"
            description={`No events or tasks due through ${lastDay.toLocaleDateString(undefined, { month: "short", day: "numeric" })}.`}
            size="inline"
          />
        </div>
      )}
      {groups.map(({ day, events: dayEvents, tasks }) => (
        <section key={formatDateParam(day)} className="sch-agenda__day" aria-label={day.toLocaleDateString(undefined, { dateStyle: "full" })}>
          <h2 className={`sch-agenda__heading ${isSameDay(day, today) ? "is-today" : ""}`}>
            <span className="sch-agenda__heading-main">{day.toLocaleDateString(undefined, { weekday: "long" })}</span>
            <span className="sch-agenda__heading-date">
              {day.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              {isSameDay(day, today) ? " · Today" : ""}
            </span>
          </h2>
          <ul className="sch-agenda__list">
            {dayEvents.map((event) => (
              <li key={`${event.id}-${formatDateParam(day)}`}>
                <button type="button" className="sch-agenda__item" onClick={() => onOpenEvent(event)}>
                  <span className="sch-agenda__time">{event.allDay ? "All day" : formatTime(event.startsAt)}</span>
                  <span className={`sch-agenda__dot ${kindClass(event.kind)}`} aria-hidden="true" />
                  <span className="sch-agenda__title">
                    <span className="sr-only">{kindLabel(event.kind)}: </span>
                    {event.title}
                  </span>
                  {event.location && <span className="sch-agenda__meta">{event.location}</span>}
                  {safeMeetingUrl(event.meetingUrl) && <Icon name="link" size={13} />}
                </button>
              </li>
            ))}
            {tasks.map((task) => (
              <li key={task.id}>
                <button type="button" className="sch-agenda__item sch-agenda__item--task" onClick={() => onOpenTask(task)}>
                  <span className="sch-agenda__time">Due</span>
                  <Icon name="check" size={13} />
                  <span className="sch-agenda__title">{task.title}</span>
                  {task.projectCode && <span className="sch-agenda__meta">{task.projectCode}</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <div className="sch-agenda__more">
        <Button variant="secondary" size="sm" loading={loadingMore} onClick={onLoadMore}>
          Show the next {AGENDA_CHUNK_DAYS} days
        </Button>
      </div>
    </div>
  );
}

/* ──────────────────────────────────── event detail ──────────────────────────────────── */

interface EventDetailProps {
  event: ScheduleEvent;
  viewerId: string | undefined;
  onRespond: (status: ResponseStatus) => void;
  responding: boolean;
  onDelete: () => void;
  onEdit: () => void;
  deleting: boolean;
  canManage: boolean;
  onReminderChange: (minutes: number | null) => void;
}

function EventDetailBody({ event, viewerId, onRespond, responding, onDelete, onEdit, deleting, canManage, onReminderChange }: EventDetailProps) {
  const viewerAttendee = event.attendees.find((a) => a.id === viewerId);
  const meetingUrl = safeMeetingUrl(event.meetingUrl);
  const zoneHint = originalZoneHint(event);
  const eventLead = event.reminderMinutes ?? DEFAULT_REMINDER_MINUTES;

  return (
    <div className="sch-detail">
      <div className="sch-detail__header">
        <span className={`sch-detail__kind-dot ${kindClass(event.kind)}`} role="img" aria-label={kindLabel(event.kind)} />
        <h2 className="sch-detail__title">{event.title}</h2>
      </div>

      <div className="sch-detail__row">
        <Icon name="clock" size={15} />
        <span>
          {event.allDay
            ? `All day · ${new Date(event.startsAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
            : `${formatDateTimeLong(event.startsAt)} – ${formatTime(event.endsAt)}`}
        </span>
      </div>

      {zoneHint && (
        <p className="sch-detail__hint">Set for {zoneHint}</p>
      )}

      {isRepeating(event) && (
        <div className="sch-detail__row">
          <Icon name="repeat" size={15} />
          <span>{event.isException ? "Repeating event, changed for this date" : "Repeating event"}</span>
        </div>
      )}

      {event.location && (
        <div className="sch-detail__row">
          <Icon name="pin" size={15} />
          <span>{event.location}</span>
        </div>
      )}

      {meetingUrl && (
        <div className="sch-detail__row">
          <Icon name="link" size={15} />
          <a className="sch-detail__join" href={meetingUrl} target="_blank" rel="noopener noreferrer">
            {joinLabel(meetingUrl)}
          </a>
        </div>
      )}

      <div className="sch-detail__row">
        <Icon name="people" size={15} />
        <span>Organised by {event.organiserName ?? "Unknown"}</span>
      </div>

      {event.description && <p className="sch-detail__description">{event.description}</p>}

      <div className="sch-detail__section">
        <h3 className="sch-detail__section-title">Attendees ({event.attendees.length})</h3>
        <div className="sch-detail__attendees">
          {event.attendees.map((a) => (
            <div key={a.id} className="sch-detail__attendee">
              <Avatar name={a.displayName} size={24} src={a.avatarUrl ?? undefined} />
              <span className="sch-detail__attendee-name">{a.displayName}</span>
              <Pill tone={RESPONSE_TONE[a.responseStatus]}>{RESPONSE_LABEL[a.responseStatus]}</Pill>
            </div>
          ))}
        </div>
      </div>

      {viewerAttendee && (
        <div className="sch-detail__section">
          <Select
            label="Remind me"
            value={event.myReminderMinutes ?? ""}
            onChange={(e) => onReminderChange(e.target.value === "" ? null : Number(e.target.value))}
          >
            <option value="">
              Same as the event ({REMINDER_CHOICES.find((c) => c.value === eventLead)?.label.toLowerCase() ?? `${eventLead} minutes before`})
            </option>
            {REMINDER_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </Select>
        </div>
      )}

      {viewerAttendee && (
        <div className="sch-detail__section">
          <h3 className="sch-detail__section-title">Your response</h3>
          <div className="sch-detail__rsvp">
            <Button
              size="sm"
              variant={viewerAttendee.responseStatus === "accepted" ? "primary" : "secondary"}
              loading={responding}
              onClick={() => onRespond("accepted")}
            >
              Accept
            </Button>
            <Button
              size="sm"
              variant={viewerAttendee.responseStatus === "tentative" ? "primary" : "secondary"}
              loading={responding}
              onClick={() => onRespond("tentative")}
            >
              Tentative
            </Button>
            <Button
              size="sm"
              variant={viewerAttendee.responseStatus === "declined" ? "danger" : "secondary"}
              loading={responding}
              onClick={() => onRespond("declined")}
            >
              Decline
            </Button>
          </div>
        </div>
      )}

      {canManage && (
        <div className="sch-detail__footer">
          <Button variant="secondary" size="sm" icon={<Icon name="edit" size={14} />} onClick={onEdit}>
            Edit
          </Button>
          <Button variant="danger" size="sm" icon={<Icon name="trash" size={14} />} loading={deleting} onClick={onDelete}>
            Delete event
          </Button>
        </div>
      )}
    </div>
  );
}

/*
 * The rhythms worth offering, as the RRULEs they are stored as.
 *
 * Matches TaskDetail.tsx's own preset list, but events repeat on the
 * calendar rather than on completion (see lib/recurrence.ts's
 * `occurrencesBetween` vs `nextOccurrenceAfter`), so unlike a task's version
 * this needs no due date to be meaningful — the event's own start anchors it.
 */
const EVENT_RECURRENCE_PRESETS: { label: string; rule: string | null }[] = [
  { label: "Does not repeat", rule: null },
  { label: "Every day", rule: "FREQ=DAILY" },
  { label: "Every week", rule: "FREQ=WEEKLY" },
  { label: "Every two weeks", rule: "FREQ=WEEKLY;INTERVAL=2" },
  { label: "Every month", rule: "FREQ=MONTHLY" },
  { label: "Every year", rule: "FREQ=YEARLY" },
];

/* ──────────────────────────────────── scope choice ──────────────────────────────────── */

const SCOPE_OPTIONS: { value: EditScope; label: string; hint: string }[] = [
  { value: "this", label: "This event", hint: "Only this date. The rest of the series stays as it is." },
  { value: "following", label: "This and following events", hint: "This date and every later one." },
  { value: "all", label: "All events", hint: "The whole series, past and future." },
];

interface ScopeChoiceProps {
  verb: "edit" | "delete";
  busy?: boolean;
  onConfirm: (scope: EditScope) => void;
  onCancel: () => void;
}

/**
 * Asked before touching one date of a repeating event, so that "delete" never
 * silently means "delete the whole series". The narrowest option is the
 * default: it is the one that can least surprise anybody.
 */
function ScopeChoice({ verb, busy, onConfirm, onCancel }: ScopeChoiceProps) {
  const [scope, setScope] = useState<EditScope>("this");
  return (
    <div className="sch-scope">
      <fieldset className="sch-scope__group">
        <legend className="sch-scope__legend">{verb === "delete" ? "Which events do you want to delete?" : "Which events do you want to change?"}</legend>
        {SCOPE_OPTIONS.map((option) => (
          <label key={option.value} className={`sch-scope__option ${scope === option.value ? "is-selected" : ""}`}>
            <input type="radio" name="sch-scope" value={option.value} checked={scope === option.value} onChange={() => setScope(option.value)} />
            <span className="sch-scope__text">
              <span className="sch-scope__label">{option.label}</span>
              <span className="sch-scope__hint">{option.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <div className="sch-modal-actions">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant={verb === "delete" ? "danger" : "primary"} loading={busy} onClick={() => onConfirm(scope)}>
          {verb === "delete" ? "Delete" : "Continue"}
        </Button>
      </div>
    </div>
  );
}

/* ──────────────────────────────────── new event form ──────────────────────────────────── */

interface NewEventFormProps {
  defaultDate: Date;
  onCreated: (event: ScheduleEvent) => void;
  onCancel: () => void;
  /** When present the form edits this event instead of creating one. */
  event?: ScheduleEvent;
  /** For a repeating event: how much of the series the edit applies to. */
  scope?: EditScope;
}

/**
 * One form for both creating and editing.
 *
 * The fields, the validation, and the all-day handling are identical; only
 * the verb differs. Two copies would drift, and the end-after-start rule is
 * exactly the kind of thing that drifts silently.
 */
function NewEventForm({ defaultDate, onCreated, onCancel, event, scope }: NewEventFormProps) {
  const { toast } = useToast();
  const { viewer } = useAuth();
  const editing = !!event;
  const [title, setTitle] = useState(event?.title ?? "");
  const [kind, setKind] = useState(event?.kind ?? "meeting");
  const [allDay, setAllDay] = useState(event?.allDay ?? false);
  const [location, setLocation] = useState(event?.location ?? "");
  const [recurrenceRule, setRecurrenceRule] = useState<string | null>(event?.recurrenceRule ?? null);
  const [meetingUrl, setMeetingUrl] = useState(event?.meetingUrl ?? "");
  // Chosen explicitly and always sent: the server would otherwise fall back to
  // whatever zone the profile holds, which may not be the one in use right now.
  const [timeZone, setTimeZone] = useState(event?.timeZone ?? viewer?.timezone ?? browserZone());
  const [reminderMinutes, setReminderMinutes] = useState(event?.reminderMinutes ?? DEFAULT_REMINDER_MINUTES);
  const zones = useMemo(() => zoneChoices(timeZone, viewer?.timezone, browserZone()), [timeZone, viewer?.timezone]);
  // One date of a series can change its words and its time, nothing structural.
  const thisOnly = scope === "this";

  const defaultStart = useMemo(() => {
    const d = new Date(defaultDate);
    d.setHours(9, 0, 0, 0);
    return d;
  }, [defaultDate]);
  const defaultEnd = useMemo(() => new Date(defaultStart.getTime() + 30 * 60_000), [defaultStart]);

  const [startValue, setStartValue] = useState(() =>
    toDateTimeLocalValue(event ? new Date(event.startsAt) : defaultStart),
  );
  const [endValue, setEndValue] = useState(() => toDateTimeLocalValue(event ? new Date(event.endsAt) : defaultEnd));
  const [dateValue, setDateValue] = useState(() =>
    formatDateParam(event ? new Date(event.startsAt) : defaultDate),
  );
  const [saving, setSaving] = useState(false);
  const [rangeError, setRangeError] = useState<string | undefined>(undefined);
  const [linkError, setLinkError] = useState<string | undefined>(undefined);

  const submit = useCallback(() => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    let startsAt: Date;
    let endsAt: Date;
    if (allDay) {
      const [y, m, d] = dateValue.split("-").map(Number);
      startsAt = new Date(y!, m! - 1, d!, 0, 0, 0);
      endsAt = new Date(y!, m! - 1, d!, 23, 59, 59);
    } else {
      startsAt = fromDateTimeLocalValue(startValue);
      endsAt = fromDateTimeLocalValue(endValue);
    }

    // Client-side mirror of the server's `endsAt > startsAt` rule, so the
    // error shows up before a round trip rather than after one.
    if (endsAt.getTime() <= startsAt.getTime()) {
      setRangeError("End time must be after the start time.");
      return;
    }
    setRangeError(undefined);
    setSaving(true);

    const trimmedLink = meetingUrl.trim();
    if (trimmedLink && !safeMeetingUrl(trimmedLink)) {
      setRangeError(undefined);
      setLinkError("Use a web link starting with http:// or https://.");
      setSaving(false);
      return;
    }
    setLinkError(undefined);

    const common = {
      title: trimmedTitle,
      // null clears a location / link that was there; undefined would leave it.
      location: location.trim() || (editing ? null : undefined),
      meetingUrl: trimmedLink || (editing ? null : undefined),
      startsAt: startsAt.toISOString(),
      endsAt: endsAt.toISOString(),
    };
    const body = thisOnly
      ? common
      : { ...common, kind, allDay, recurrenceRule, timeZone, reminderMinutes };
    // The occurrence is addressed by the slot the rule put it in, not by where
    // it currently sits.
    const occurrence = editing && scope && event!.originalStart ? { scope, occurrenceStart: event!.originalStart } : {};

    const request = editing
      ? api.patch<{ event: ScheduleEvent }>(`/events/${eventKey(event!)}`, { ...body, ...occurrence })
      : api.post<{ event: ScheduleEvent }>("/events", body);

    request
      .then((res) => onCreated(res.event))
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.code === "VALIDATION_ERROR") {
          setRangeError(error.message);
          return;
        }
        toast(
          error instanceof ApiError ? error.message : `Could not ${editing ? "save" : "create"} event.`,
          { tone: "danger" },
        );
      })
      .finally(() => setSaving(false));
  }, [title, kind, allDay, location, recurrenceRule, meetingUrl, timeZone, reminderMinutes, thisOnly, scope, dateValue, startValue, endValue, onCreated, toast, editing, event]);

  return (
    <form
      className="sch-new-event"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus required />

      {thisOnly && <p className="sch-new-event__note">Changing this date only. Other dates in the series are not affected.</p>}

      {!thisOnly && (
        <Select label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
          {KIND_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      )}

      {!thisOnly && (
      <div className="sch-new-event__row">
        <Checkbox label="All day" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />

        {/*
         * Offered as a handful of ordinary rhythms rather than an RRULE box —
         * the string is a storage format, not something to ask anyone to
         * write. A rule this list does not cover (set elsewhere, or by hand
         * on an event being edited) is kept and shown as "Custom" rather than
         * silently rewritten to the nearest option.
         */}
        <Select
          label="Repeats"
          value={
            EVENT_RECURRENCE_PRESETS.some((p) => p.rule === recurrenceRule) ? (recurrenceRule ?? "") : "custom"
          }
          onChange={(e) => {
            const value = e.target.value;
            if (value === "custom") return;
            setRecurrenceRule(value || null);
          }}
        >
          {EVENT_RECURRENCE_PRESETS.map((preset) => (
            <option key={preset.label} value={preset.rule ?? ""}>
              {preset.label}
            </option>
          ))}
          {recurrenceRule && !EVENT_RECURRENCE_PRESETS.some((p) => p.rule === recurrenceRule) && (
            <option value="custom">Custom</option>
          )}
        </Select>
      </div>
      )}

      {allDay ? (
        <Input type="date" label="Date" value={dateValue} onChange={(e) => setDateValue(e.target.value)} required />
      ) : (
        <div className="sch-new-event__times">
          <Input
            type="datetime-local"
            label="Starts"
            value={startValue}
            onChange={(e) => {
              setStartValue(e.target.value);
              setRangeError(undefined);
            }}
            required
          />
          <Input
            type="datetime-local"
            label="Ends"
            value={endValue}
            error={rangeError}
            onChange={(e) => {
              setEndValue(e.target.value);
              setRangeError(undefined);
            }}
            required
          />
        </div>
      )}
      {allDay && rangeError && <p className="sch-new-event__error">{rangeError}</p>}

      <Input label="Location (optional)" value={location} onChange={(e) => setLocation(e.target.value)} />

      <Input
        type="url"
        inputMode="url"
        label="Meeting link (optional)"
        placeholder="https://meet.google.com/…"
        value={meetingUrl}
        error={linkError}
        onChange={(e) => {
          setMeetingUrl(e.target.value);
          setLinkError(undefined);
        }}
      />

      {!thisOnly && (
        <div className="sch-new-event__times">
          <Select label="Time zone" value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
            {zones.map((zone) => (
              <option key={zone} value={zone}>
                {zone.replace(/_/g, " ")}
              </option>
            ))}
          </Select>
          <Select label="Reminder" value={reminderMinutes} onChange={(e) => setReminderMinutes(Number(e.target.value))}>
            {REMINDER_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </Select>
        </div>
      )}

      <div className="sch-new-event__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={saving} disabled={!title.trim()}>
          {editing ? "Save changes" : "Create event"}
        </Button>
      </div>
    </form>
  );
}

/* ──────────────────────────────────── main view ──────────────────────────────────── */

/** Below this width the event detail uses the bottom Sheet; at or above it, the
 * inline desktop panel. Matches the 900px breakpoint every other route switches
 * its mobile layout at (see work.css, task-detail.css, chat.css). */
const MOBILE_QUERY = "(max-width: 900px)";

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

export function Schedule() {
  usePageTitle("Schedule");
  const [searchParams, setSearchParams] = useSearchParams();
  const { viewer } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // URL is the single source of truth for view + focused date, matching
  // Work.tsx's filters-in-URL pattern — a reload or a shared link reproduces
  // exactly what was on screen.
  const viewParam = searchParams.get("view") as ViewMode | null;
  const view: ViewMode = viewParam && VIEW_MODES.includes(viewParam) ? viewParam : "month";
  const focusedDate = useMemo(() => parseDateParam(searchParams.get("date")), [searchParams]);

  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [dueTasks, setDueTasks] = useState<DueTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createDate, setCreateDate] = useState<Date>(focusedDate);
  const [responding, setResponding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ScheduleEvent | null>(null);
  // Which part of a repeating series an edit applies to; unset for one-off events.
  const [editScope, setEditScope] = useState<EditScope | undefined>(undefined);
  // The "this / following / all" question, shown before an edit form or a delete.
  const [scopePrompt, setScopePrompt] = useState<"edit" | "delete" | null>(null);
  const [agendaDays, setAgendaDays] = useState(AGENDA_CHUNK_DAYS);

  const updateParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined || value === "") next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // The fetch window always covers whole weeks around the focused range, so
  // paging within the visible grid never needs a second round trip.
  const { rangeStart, rangeEnd } = useMemo(() => {
    if (view === "day") return { rangeStart: focusedDate, rangeEnd: addDays(focusedDate, 1) };
    if (view === "agenda") return { rangeStart: focusedDate, rangeEnd: addDays(focusedDate, agendaDays) };
    if (view === "week") {
      const start = startOfWeek(focusedDate);
      return { rangeStart: start, rangeEnd: addDays(start, 7) };
    }
    const { start, end } = monthGridRange(startOfMonth(focusedDate));
    return { rangeStart: start, rangeEnd: end };
  }, [view, focusedDate, agendaDays]);

  // Paging the agenda to another start date begins again from a fortnight.
  useEffect(() => {
    setAgendaDays(AGENDA_CHUNK_DAYS);
  }, [focusedDate, view]);

  /**
   * Reload the whole visible range.
   *
   * Needed wherever a repeating event changes. The server expands a repeating
   * event into one entry per occurrence in view, so a single edit can add,
   * move or remove several of the rows on screen at once — there is no one row
   * to patch in place. Refetching is the only honest way to reflect that.
   */
  const reloadEvents = useCallback(async () => {
    const params = new URLSearchParams({ from: rangeStart.toISOString(), to: rangeEnd.toISOString() });
    try {
      const res = await api.get<{ events: ScheduleEvent[] }>(`/events?${params.toString()}`);
      setEvents(res.events);
    } catch {
      /* the grid keeps what it has; the next range change will retry */
    }
  }, [rangeStart, rangeEnd]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams({
      from: rangeStart.toISOString(),
      to: rangeEnd.toISOString(),
    });

    api
      .get<{ events: ScheduleEvent[] }>(`/events?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setEvents(res.events);
      })
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load the schedule.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rangeStart, rangeEnd, toast]);

  /*
   * A second, independent fetch rather than folding this into the events
   * request: the two are unrelated resources with unrelated failure modes,
   * and a task-list outage should not blank the whole calendar the way
   * merging the requests would.
   */
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({
      dueAfter: rangeStart.toISOString(),
      dueBefore: rangeEnd.toISOString(),
      // Open work only — a finished or abandoned task is not something to
      // still be reminded of on the day it was due.
      status: "todo,in_progress,review",
    });

    api
      .get<{ tasks: DueTask[] }>(`/tasks?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setDueTasks(res.tasks);
      })
      .catch(() => {
        // Silent: the calendar's own events still loaded, and a task-due chip
        // is additive — worth losing quietly rather than blocking the page
        // with a second toast on top of whatever the events fetch already
        // reported.
        if (!cancelled) setDueTasks([]);
      });

    return () => {
      cancelled = true;
    };
  }, [rangeStart, rangeEnd]);

  const refetchEvent = useCallback((eventId: string) => {
    api
      .get<{ event: ScheduleEvent }>(`/events/${eventId}`)
      .then((res) => {
        setEvents((prev) => prev.map((e) => (e.id === eventId ? res.event : e)));
      })
      .catch(() => undefined);
  }, []);

  /*
   * Focus a single event named by `?event=` — the destination a notification
   * deep-links to when it can't rely on the current view already covering the
   * right week/month.
   *
   * If the event is already among the currently loaded (visible-range) rows,
   * just select it. Otherwise it is most likely outside the fetched range
   * (e.g. a reminder for next month while the grid shows this one), so fetch
   * it directly and merge it into `events` — the Sheet only needs the row to
   * exist, not for it to belong to the visible grid. The param is consumed
   * once and then stripped, same as Chat.tsx does for `?message=`, so a
   * refresh doesn't re-trigger the same jump.
   */
  useEffect(() => {
    const eventId = searchParams.get("event");
    if (!eventId) return;

    const inRange = events.find((e) => e.id === eventId);
    if (inRange) {
      setSelectedEventId(eventId);
      updateParams({ event: undefined });
      return;
    }

    // Wait for the range fetch to settle before deciding the event is truly
    // missing from `events` — otherwise this fires on the initial empty
    // array and always falls through to a fetch even when it was about to
    // load into range on its own.
    if (loading) return;

    let cancelled = false;
    api
      .get<{ event: ScheduleEvent }>(`/events/${eventId}`)
      .then((res) => {
        if (cancelled) return;
        setEvents((prev) => (prev.some((e) => e.id === eventId) ? prev : [...prev, res.event]));
        setSelectedEventId(eventId);
      })
      .catch(() => {
        if (!cancelled) toast("That event could not be found.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) updateParams({ event: undefined });
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams, events, loading, updateParams, toast]);

  const goToday = useCallback(() => {
    updateParams({ date: formatDateParam(startOfDay(new Date())) });
  }, [updateParams]);

  const goPrev = useCallback(() => {
    const next =
      view === "day"
        ? addDays(focusedDate, -1)
        : view === "agenda"
          ? addDays(focusedDate, -AGENDA_CHUNK_DAYS)
          : view === "week"
            ? addDays(focusedDate, -7)
            : new Date(focusedDate.getFullYear(), focusedDate.getMonth() - 1, 1);
    updateParams({ date: formatDateParam(next) });
  }, [view, focusedDate, updateParams]);

  const goNext = useCallback(() => {
    const next =
      view === "day"
        ? addDays(focusedDate, 1)
        : view === "agenda"
          ? addDays(focusedDate, AGENDA_CHUNK_DAYS)
          : view === "week"
            ? addDays(focusedDate, 7)
            : new Date(focusedDate.getFullYear(), focusedDate.getMonth() + 1, 1);
    updateParams({ date: formatDateParam(next) });
  }, [view, focusedDate, updateParams]);

  const selectedEvent = useMemo(() => events.find((e) => e.id === selectedEventId) ?? null, [events, selectedEventId]);

  const handleRespond = useCallback(
    (status: ResponseStatus) => {
      if (!selectedEvent || !viewer) return;
      const eventId = selectedEvent.id;
      const previous = selectedEvent;
      // Optimistic: flip just the viewer's own attendee row, reconciled below.
      setEvents((prev) =>
        prev.map((e) =>
          e.id === eventId ? { ...e, attendees: e.attendees.map((a) => (a.id === viewer.id ? { ...a, responseStatus: status } : a)) } : e,
        ),
      );
      setResponding(true);
      api
        .post(`/events/${eventKey(selectedEvent)}/respond`, { responseStatus: status })
        // The response endpoint only echoes back the one attendee row, not the
        // whole event, so a refetch is what keeps `respondedAt` and any
        // server-side timestamp truthful rather than trusting the optimistic guess.
        .then(() => refetchEvent(eventKey(selectedEvent)))
        .catch((error: unknown) => {
          setEvents((prev) => prev.map((e) => (e.id === previous.id ? previous : e)));
          toast(error instanceof ApiError ? error.message : "Could not save your response.", { tone: "danger" });
        })
        .finally(() => setResponding(false));
    },
    [selectedEvent, viewer, toast, refetchEvent],
  );

  const handleDelete = useCallback(
    (scope?: EditScope) => {
      if (!selectedEvent) return;
      setDeleting(true);
      const query = new URLSearchParams();
      if (scope) {
        query.set("scope", scope);
        if (scope !== "all" && selectedEvent.originalStart) query.set("occurrenceStart", selectedEvent.originalStart);
      }
      const suffix = query.toString() ? `?${query.toString()}` : "";
      api
        .delete(`/events/${eventKey(selectedEvent)}${suffix}`)
        .then(() => {
          if (scope === "this" || scope === "following") {
            // Which rows disappear depends on the scope, so ask the server.
            void reloadEvents();
          } else {
            // Filtered by template id: deleting a repeating event removes every
            // occurrence of it currently on screen, not just the one clicked.
            setEvents((prev) => prev.filter((e) => eventKey(e) !== eventKey(selectedEvent)));
          }
          setSelectedEventId(null);
          setDeleteConfirmOpen(false);
          setScopePrompt(null);
          toast(scope === "this" ? "Event cancelled." : "Event deleted.", { tone: "ok" });
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not delete event.", { tone: "danger" });
        })
        .finally(() => setDeleting(false));
    },
    [selectedEvent, toast, reloadEvents],
  );

  const handleReminderChange = useCallback(
    (minutes: number | null) => {
      if (!selectedEvent) return;
      const key = eventKey(selectedEvent);
      const previous = selectedEvent.myReminderMinutes ?? null;
      const apply = (value: number | null) =>
        setEvents((prev) => prev.map((e) => (eventKey(e) === key ? { ...e, myReminderMinutes: value } : e)));
      apply(minutes);
      api.put(`/events/${key}/reminder`, { reminderMinutes: minutes }).catch((error: unknown) => {
        apply(previous);
        toast(error instanceof ApiError ? error.message : "Could not save your reminder.", { tone: "danger" });
      });
    },
    [selectedEvent, toast],
  );

  const handleCreated = useCallback(
    (event: ScheduleEvent) => {
      // A repeating event arrives as the single stored row; the occurrences
      // only exist once the server expands them, so ask for them.
      if (event.recurrenceRule) void reloadEvents();
      else setEvents((prev) => [...prev, event]);
      setCreating(false);
      toast("Event created.", { tone: "ok" });
    },
    [toast, reloadEvents],
  );

  const handleUpdated = useCallback(
    (event: ScheduleEvent) => {
      if (event.recurrenceRule || editScope) {
        /*
         * A repeating event is expanded server-side into one row per occurrence,
         * so an edit can add, move or remove several of the rows on screen at
         * once. There is no single row to swap — reload the range.
         */
        void reloadEvents();
      } else {
        // Replaced in place: the event keeps its position in the grid rather
        // than disappearing and reappearing, which is what a refetch looks like.
        setEvents((prev) => prev.map((e) => (e.id === event.id ? event : e)));
      }
      // The row that was open may no longer exist under that id (a split series
      // gives the later dates a new one), so close it rather than show stale data.
      if (editScope) setSelectedEventId(null);
      setEditingEvent(null);
      setEditScope(undefined);
      toast("Event updated.", { tone: "ok" });
    },
    [toast, reloadEvents, editScope],
  );

  const title =
    view === "day"
      ? focusedDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
      : view === "agenda"
        ? "Agenda"
        : view === "week"
          ? formatWeekTitle(startOfWeek(focusedDate))
          : formatMonthTitle(focusedDate);
  const canManageSelected = !!(selectedEvent && viewer && (selectedEvent.organiserId === viewer.id || viewer.role === "admin"));

  const detailBody = selectedEvent && (
    <EventDetailBody
      event={selectedEvent}
      viewerId={viewer?.id}
      onRespond={handleRespond}
      responding={responding}
      onDelete={() => (isRepeating(selectedEvent) ? setScopePrompt("delete") : setDeleteConfirmOpen(true))}
      onEdit={() => {
        if (isRepeating(selectedEvent)) setScopePrompt("edit");
        else {
          setEditScope(undefined);
          setEditingEvent(selectedEvent);
        }
      }}
      deleting={deleting}
      canManage={canManageSelected}
      onReminderChange={handleReminderChange}
    />
  );

  return (
    <div className="sch-shell">
      <PageHeader
        title={title}
        leading={
          <div className="sch-topbar__nav">
            <IconButton icon={<Icon name="chevron-left" size={16} />} label="Previous" size="sm" onClick={goPrev} />
            <Button variant="secondary" size="sm" className="sch-topbar__today" onClick={goToday}>
              Today
            </Button>
            <IconButton icon={<Icon name="chevron-right" size={16} />} label="Next" size="sm" onClick={goNext} />
          </div>
        }
        actions={
          <div className="sch-topbar__actions">
            <SegmentedControl
              aria-label="Calendar view"
              value={view}
              onChange={(value) => updateParams({ view: value === "month" ? undefined : value })}
              options={[
                { value: "month", label: "Month" },
                { value: "week", label: "Week" },
                { value: "day", label: "Day" },
                { value: "agenda", label: "Agenda" },
              ]}
            />
            <Button
              className="sch-topbar__new-event"
              icon={<Icon name="plus" size={15} />}
              onClick={() => {
                setCreateDate(focusedDate);
                setCreating(true);
              }}
            >
              New event
            </Button>
          </div>
        }
      />

      <div className="sch-content">
        {loading && !(view === "agenda" && events.length > 0) ? (
          <div className="sch-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="sch-loading__row">
                <Skeleton height={14} width="20%" />
                <SkeletonText lines={1} />
              </div>
            ))}
          </div>
        ) : events.length === 0 && dueTasks.length === 0 && view === "month" ? (
          // The grid itself still renders (empty cells are meaningful — you can
          // see there's nothing on the 14th), so this sits above it rather than
          // replacing it, unlike Work's full-panel EmptyState swap.
          <>
            <div className="sch-empty-banner">
              <EmptyState
                icon="calendar"
                title="Nothing on the calendar this month"
                description="Create an event to get started."
                size="inline"
              />
            </div>
            <MonthView
              focusedDate={focusedDate}
              events={events}
              dueTasks={dueTasks}
              onOpenEvent={(event) => setSelectedEventId(event.id)}
              onOpenDay={(date) => {
                setCreateDate(date);
                setCreating(true);
              }}
              onOpenTask={(task) => navigate(`/work/task/${task.id}`)}
            />
          </>
        ) : view === "month" ? (
          <MonthView
            focusedDate={focusedDate}
            events={events}
            dueTasks={dueTasks}
            onOpenEvent={(event) => setSelectedEventId(event.id)}
            onOpenDay={(date) => {
              setCreateDate(date);
              setCreating(true);
            }}
            onOpenTask={(task) => navigate(`/work/task/${task.id}`)}
          />
        ) : view === "agenda" ? (
          <AgendaView
            start={focusedDate}
            days={agendaDays}
            events={events}
            dueTasks={dueTasks}
            loadingMore={loading}
            onLoadMore={() => setAgendaDays((n) => n + AGENDA_CHUNK_DAYS)}
            onOpenEvent={(event) => setSelectedEventId(event.id)}
            onOpenTask={(task) => navigate(`/work/task/${task.id}`)}
          />
        ) : (
          <WeekView
            weekStart={view === "day" ? focusedDate : startOfWeek(focusedDate)}
            dayCount={view === "day" ? 1 : 7}
            events={events}
            dueTasks={dueTasks}
            onOpenEvent={(event) => setSelectedEventId(event.id)}
            onOpenTask={(task) => navigate(`/work/task/${task.id}`)}
          />
        )}
      </div>

      {/* Desktop: inline panel beside the grid. Mobile: bottom Sheet. Both are
          driven by the same `selectedEvent` state so there is one source of
          truth for "which event is open", per the spec's split. */}
      {!isMobile && selectedEvent && (
        <div className="sch-panel-backdrop" onClick={() => setSelectedEventId(null)}>
          <div className="sch-panel" onClick={(e) => e.stopPropagation()}>
            <div className="sch-panel__header">
              <IconButton icon={<Icon name="close" size={16} />} label="Close" size="sm" onClick={() => setSelectedEventId(null)} />
            </div>
            {detailBody}
          </div>
        </div>
      )}

      {isMobile && (
        <Sheet open={!!selectedEvent} onClose={() => setSelectedEventId(null)} title="Event">
          {detailBody}
        </Sheet>
      )}

      {isMobile ? (
        <Sheet open={creating} onClose={() => setCreating(false)} title="New event">
          <NewEventForm defaultDate={createDate} onCreated={handleCreated} onCancel={() => setCreating(false)} />
        </Sheet>
      ) : (
        <Modal open={creating} onClose={() => setCreating(false)} title="New event">
          <NewEventForm defaultDate={createDate} onCreated={handleCreated} onCancel={() => setCreating(false)} />
        </Modal>
      )}

      {/* Keyed by event id so reopening on a different event rebuilds the
          form rather than reusing the previous one's state. */}
      {editingEvent &&
        (isMobile ? (
          <Sheet open onClose={() => { setEditingEvent(null); setEditScope(undefined); }} title="Edit event">
            <NewEventForm
              key={editingEvent.id}
              event={editingEvent}
              scope={editScope}
              defaultDate={createDate}
              onCreated={handleUpdated}
              onCancel={() => {
                setEditingEvent(null);
                setEditScope(undefined);
              }}
            />
          </Sheet>
        ) : (
          <Modal open onClose={() => { setEditingEvent(null); setEditScope(undefined); }} title="Edit event">
            <NewEventForm
              key={editingEvent.id}
              event={editingEvent}
              scope={editScope}
              defaultDate={createDate}
              onCreated={handleUpdated}
              onCancel={() => {
                setEditingEvent(null);
                setEditScope(undefined);
              }}
            />
          </Modal>
        ))}

      <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} title="Delete event?">
        <p className="sch-modal-copy">This permanently removes the event for every attendee. This cannot be undone.</p>
        <div className="sch-modal-actions">
          <Button variant="secondary" onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleting} onClick={() => handleDelete()}>
            Delete event
          </Button>
        </div>
      </Modal>

      <Modal
        open={scopePrompt !== null && !!selectedEvent}
        onClose={() => setScopePrompt(null)}
        title={scopePrompt === "delete" ? "Delete repeating event" : "Edit repeating event"}
      >
        {scopePrompt && selectedEvent && (
          <ScopeChoice
            verb={scopePrompt}
            busy={deleting}
            onCancel={() => setScopePrompt(null)}
            onConfirm={(scope) => {
              if (scopePrompt === "delete") {
                handleDelete(scope);
              } else {
                setEditScope(scope);
                setEditingEvent(selectedEvent);
                setScopePrompt(null);
              }
            }}
          />
        )}
      </Modal>
    </div>
  );
}
